'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from 'styled-system/css';
import { useInterval } from 'usehooks-ts';

import { Alert } from '@shaojiang/components/ui/alert';
import usePrevious from '@shaojiang/hooks/usePrevious';

import { ImageElement, PanoramaCanvasProps, Point } from './types';
import { floatEqual, limitWithinRange, tryToLoadImage } from './utils';

const MAX_RATIO = 1.5;

/**
 * When user resizes the window, we want to resize the canvas, but setting canvas.width will clear the canvas by default.
 * Even if we redraw the canvas immediately, there will be a flicker.
 * To avoid this, we set a fixed canvas.width to some unrealistic value (e.g. 12000px).
 * Also, there is a variable `visibleCanvasWidth` which is visible portion of the canvas. Overflowed portion will be hidden.
 * When user resizes the window, we update `visibleCanvasWidth` to the parent element width and redraw the canvas.
 */
const REAL_CANVAS_WIDTH = 12000;

/**
 * This component draws a panorama image on a canvas.
 * The panorama image is split into multiple images, which are painted on canvas side-by-side.
 *
 * To paint on canvas, we maintain
 *  1. xCenter: center of the original panorama image. The value is between 0 and PANO_WIDTH.
 *  2. ratio: ratio of the painted image size over the original image size (for scaling/zooming).
 *
 *  Performance tuning:
 *  1. An image is only loaded when it's to be painted on canvas.
 *  2. Canvas is unrealistically wide (12000px), but only visible portion is painted.
 *
 * @param images An array of image URLs.
 * @param pannable Whether the canvas is pannable.
 * @param rotating Whether the canvas is rotating.
 * @param center The center point of the visible window.
 * @param zoom The zoom ratio.
 * @param panoWidth The Pano width (original image).
 * @param panoHeight The Pano height (original image).
 * @param canvasWidth The width of the canvas.
 * @param canvasHeight The height of the canvas.
 * @param onUpdate Callback when the canvas is updated.
 * @param onVisibleCanvasWidthChanged Callback when the visible canvas width is changed (zoomed or resized).
 */
const PanoramaCanvas = ({
  images,
  pannable = true,
  rotating = false,
  center,
  zoom = 1,
  panoWidth = 0,
  panoHeight = 0,
  canvasWidth = 0,
  canvasHeight = 600,
  onUpdate,
  onVisibleCanvasWidthChanged,
}: PanoramaCanvasProps) => {
  const minimalRatio = canvasHeight / panoHeight;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [centerPoint, setCenterPoint] = useState<Point>(center);
  const [ratio, setRatio] = useState(Math.max(minimalRatio, zoom));
  const [dragStart, setDragStart] = useState<Point | null>(null);
  const [panning, setPanning] = useState(false);
  const [imageElements, setImageElements] = useState<ImageElement[]>([]);
  const [visibleCanvasWidth, setVisibleCanvasWidth] = useState(
    Math.min(panoWidth * minimalRatio, canvasWidth),
  );

  const doi = images.length;
  const singleImageWidth = panoWidth / doi;
  const minCenterY = useMemo(() => canvasHeight / 2 / ratio, [ratio, canvasHeight]);
  const maxCenterY = panoHeight - minCenterY;
  const previousCenter = usePrevious(center);
  const small = canvasHeight <= 100;
  const _debug = process.env.NODE_ENV === 'development';

  const handleLoadedImage = useCallback((image: ImageElement) => {
    setImageElements((prevImageElements) => {
      image.ready = true;
      prevImageElements[image.index] = image;
      return [...prevImageElements];
    });
  }, []);

  const updateCanvasWidth = useCallback(() => {
    const canvas = canvasRef?.current;
    if (canvas && !canvasWidth) {
      setVisibleCanvasWidth(
        Math.min(canvas?.parentElement?.getBoundingClientRect().width || 0, panoWidth * ratio),
      );
    }
  }, [canvasWidth, panoWidth, ratio]);

  useEffect(() => {
    onVisibleCanvasWidthChanged && onVisibleCanvasWidthChanged(visibleCanvasWidth);
  }, [visibleCanvasWidth, onVisibleCanvasWidthChanged]);

  // When canvas changes, setup the event listener for window resize
  useEffect(() => {
    updateCanvasWidth();
    window.addEventListener('resize', updateCanvasWidth);
    return () => window.removeEventListener('resize', updateCanvasWidth);
  }, [canvasRef, updateCanvasWidth]);

  useInterval(
    () => {
      setCenterPoint((point) => ({
        ...point,
        x: (point.x + 5) % panoWidth,
      }));
    },
    rotating ? 50 : null,
  );

  useEffect(() => {
    // Only if the object values change, update the state.
    if (floatEqual(previousCenter?.x, center?.x) && floatEqual(previousCenter?.y, center?.y)) {
      return;
    }

    setCenterPoint((centerPoint) => ({
      ...centerPoint,
      ...center,
    }));
  }, [previousCenter, center]);

  useEffect(() => {
    if (!zoom) {
      return;
    }
    setRatio(limitWithinRange(zoom, minimalRatio, MAX_RATIO));
    setCenterPoint((point) => ({
      x: point.x,
      y: limitWithinRange(point.y, minCenterY, maxCenterY),
    }));
  }, [zoom, minimalRatio, maxCenterY, minCenterY]);

  useEffect(() => {
    // Create an array of image elements. Do not load the images yet, but store the src as `imgSrc`.
    const imgElements = images.map((imgSrc, i) => {
      const img = new Image() as ImageElement;
      img.imgSrc = imgSrc;
      img.index = i;
      img.onload = () => handleLoadedImage(img);
      return img;
    });
    setImageElements(imgElements);
  }, [images, handleLoadedImage]);

  /**
   * Repaint the canvas.
   */
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || imageElements.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, visibleCanvasWidth, canvasHeight);
    ctx.font = `${small ? 12 : 20}px Arial`;
    ctx.fillStyle = '#ccc';

    const canvasWidthWoProportion = visibleCanvasWidth / ratio;

    // Original X coordinate of the canvas left-boundary to paint (based on the whole panorama).
    let panoStartX = (centerPoint.x - canvasWidthWoProportion / 2 + panoWidth) % panoWidth;
    // Original X coordinate of the canvas right-boundary to paint (based on the whole panorama).
    const canvasEndX = panoStartX + canvasWidthWoProportion;
    // Pixel X coordinate of the current starting point to paint (based on the canvas).
    let canvasDrawStartX = 0;
    // Pixel X coordinate of the current image to start painting (based on the image).
    // The first image may need to paint from the middle.
    let singleImageStartX = panoStartX % singleImageWidth;
    // The index of the first image to paint
    const firstImageIndex = Math.floor(panoStartX / singleImageWidth) % doi;
    // The index of the image to paint.
    let imgIndex = firstImageIndex;
    let _debugPaintedCount = 0;

    while (panoStartX < canvasEndX) {
      imgIndex = Math.floor(panoStartX / singleImageWidth) % doi;
      const img = imageElements[imgIndex];
      const sWidth = Math.min(singleImageWidth - singleImageStartX, canvasEndX - panoStartX);
      tryToLoadImage(imageElements, imgIndex);

      _debug &&
        console.log(
          'img',
          img,
          '\n',
          'img.ready',
          img.ready,
          '\n',
          'sx',
          Math.floor(singleImageStartX),
          '\n',
          'sy',
          centerPoint.y - canvasHeight / 2 / ratio,
          '\n',
          'sWidth',
          Math.ceil(sWidth),
          '\n',
          'sHeight',
          canvasHeight / ratio,
          '\n',
          'dx',
          Math.floor(canvasDrawStartX),
          '\n',
          'dy',
          0,
          '\n',
          'dWidth',
          Math.ceil(sWidth * ratio),
          '\n',
          'dHeight',
          canvasHeight,
          '\n',
        );
      // drawImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight)
      // @see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage
      img.ready
        ? ctx.drawImage(
            img,
            Math.floor(singleImageStartX),
            centerPoint.y - canvasHeight / 2 / ratio,
            Math.ceil(sWidth),
            canvasHeight / ratio,
            Math.floor(canvasDrawStartX),
            0,
            Math.ceil(sWidth * ratio) + 1,
            canvasHeight,
          )
        : ctx.fillText(
            'Loading ...',
            Math.floor(canvasDrawStartX + (sWidth * ratio) / 2 - (small ? 25 : 50)),
            canvasHeight / 2 + (small ? 5 : -20),
          );

      // Move to the next image, which paint from its left-boundary.
      panoStartX += sWidth;
      canvasDrawStartX += sWidth * ratio;
      singleImageStartX = 0;

      if (_debugPaintedCount++ > doi) {
        console.error('Infinite loop detected');
        break;
      }
    }

    // Buffer the previous and next images at the background
    tryToLoadImage(imageElements, (firstImageIndex - 1 + doi) % doi);
    tryToLoadImage(imageElements, (imgIndex + 1) % doi);
  }, [
    centerPoint.x,
    centerPoint.y,
    imageElements,
    visibleCanvasWidth,
    ratio,
    singleImageWidth,
    doi,
    _debug,
    canvasHeight,
    panoWidth,
    small,
  ]);

  const previousOnUpdate = usePrevious(onUpdate);
  useEffect(() => {
    if (previousOnUpdate === onUpdate) {
      onUpdate && onUpdate(centerPoint, ratio);
    }
  }, [centerPoint, ratio, previousOnUpdate, onUpdate]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!pannable) return;
    setDragStart({
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!pannable) return;
    if (dragStart) {
      setPanning(true);
      const deltaX = e.clientX - dragStart.x;
      const deltaY = dragStart.y - e.clientY;
      setCenterPoint({
        x: (centerPoint.x - deltaX / ratio + panoWidth) % panoWidth,
        y: limitWithinRange(centerPoint.y + deltaY / ratio, minCenterY, maxCenterY),
      });
      setDragStart({
        x: e.clientX,
        y: e.clientY,
      });
    }
  };

  const handleMouseUp = () => {
    if (!pannable) return;
    setPanning(false);
    setDragStart(null);
  };

  return (
    <>
      {_debug && (
        <Alert.Root mb='4' borderColor='info.main'>
          <Alert.Content>
            <Alert.Title>Variables:</Alert.Title>
            <ul className={css({ textStyle: 'sm', listStyle: 'disc', pl: '5' })}>
              <li>centerPoint: {JSON.stringify(centerPoint)}</li>
              <li>prop.zoom: {zoom}</li>
              <li>
                ratio: {ratio}; minimalRatio: {minimalRatio}
              </li>
            </ul>
          </Alert.Content>
        </Alert.Root>
      )}
      <div
        style={{
          backgroundColor: '#131824',
          overflow: 'hidden',
          width: canvasWidth ? `${canvasWidth}px` : 'auto',
        }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          height={canvasHeight}
          width={REAL_CANVAS_WIDTH}
          inputMode='none'
          style={{
            height: `${canvasHeight}px`,
            cursor: pannable ? (panning ? 'grabbing' : dragStart ? 'grab' : 'default') : 'default',
          }}
        />
      </div>
    </>
  );
};

export default React.memo(PanoramaCanvas);
